import dynamic from 'next/dynamic';
import {Image} from '@jobseeker/lotus/components/Image';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import Typography from '@mui/material/Typography';
import {AiFillAndroid, AiFillApple} from 'react-icons/ai';

import {Section, Grid, Box} from '@jobseeker/lotus/foundations';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import InjectSearchOverlay from '@components/SearchInjection';
import {handleTracking, openUrl, deferTask} from '@jobseeker/common-core/utils';
import type {DownloadAppType} from '../types';
import type {AppState} from '@jobseeker/common-core/types';
import {getOldLink, isCSR} from '@jobseeker/lotus/utils';
import {useRouter} from 'next/router';
import {AppleLogoWhite, PlayStore} from '@jobseeker/lotus/assets/icons';
import HomePageFooter from '@components/HomeV2/homePageFooter';
import {VersionVariants} from '@jobseeker/lotus/enums';

const Button = dynamic(() => import('@jobseeker/lotus/elements/Button'));

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const DownloadApp = (props: DownloadAppType) => {
  const router = useRouter();
  const {
    appImage,
    dotSvg,
    heading,
    backgroundImg: {large, small},
    theme,
    downloadText,
    isAboveMobile,
    downloadAppButton,
    downloadAppContent,
    downloadAppQr,
  } = props || {};
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  const isBelowActualMobile = useIsCustomWidth(281);

  const {searchOverlay, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  type ButtonProps = {
    logoSrc: string;
    logoAlt: string;
    first: string;
    second: string;
    forPlayStore: boolean;
    imgWidth: number;
    imgHeight: number;
    handleClick: () => void;
  };
  const ButtonFun: React.FC<ButtonProps> = ({
    logoSrc,
    logoAlt,
    first,
    second,
    forPlayStore,
    imgWidth,
    imgHeight,
    handleClick,
  }) => {
    return (
      <Box
        onClick={handleClick}
        display={'flex'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        borderRadius={2}
        p={0.75}
        pl={forPlayStore ? 0.875 : 0.75}
        pr={1.875}
        sx={{
          backgroundColor: 'text.primary',
          cursor: 'pointer',
          width: '142px',
          height: '44px',
        }}
      >
        <Box sx={{marginRight: '4px'}} display={'flex'}>
          <Image
            src={logoSrc}
            alt={logoAlt}
            width={imgWidth}
            height={imgHeight}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'flex-start'}
        >
          <Typography
            variant="body2"
            fontSize={11}
            color={'#fff'}
            lineHeight={'8px'}
            marginBottom={'4px'}
          >
            {first}
          </Typography>
          <Typography
            variant="subtitle2"
            fontSize={15}
            color={'#fff'}
            lineHeight={'8px'}
          >
            {second}
          </Typography>
        </Box>
      </Box>
    );
  };
  return (
    <Section
      sx={{
        background: isAppIimjobs ? {xxs: small, md: large} : 'none',
      }}
    >
      <InjectSearchOverlay isSearchEnabled={searchOverlay} />
      <Grid
        container
        width="90%"
        sx={{
          px: {xxs: 3, sm: 3, md: 5, lg: 8},
          paddingTop: {xxs: theme.spacing(1.25), sm: theme.spacing(2.5)},
          margin: {
            xxs: `${theme.spacing(6.25)} auto 0`,
            md: `${theme.spacing(17.5)} auto 0`,
          },
        }}
      >
        <Grid
          item
          width="100%"
          display="inline-block"
          margin="0 auto"
          paddingBottom={theme.spacing(8.125)}
        >
          <Box
            sx={{
              float: {xxs: '', md: 'right'},
              width: {xxs: '100%', md: '55%'},
              color: 'secondary.main',
              margin: {
                xxs: `${theme.spacing(3.75)} auto`,
                md: isAppIimjobs ? `${theme.spacing(2.75)} auto` : '-1px auto',
              },
              textAlign: {xxs: 'center', md: 'left'},
              lineHeight: 1,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: isAppIimjobs
                  ? {
                      xxs: theme.typography.pxToRem(24),
                      md: theme.typography.pxToRem(28),
                    }
                  : '45px',
                marginLeft: theme.spacing(0.125),
                fontStyle: !isAppIimjobs ? 'Nunito Sans' : 'Poppins',
                fontWeight: isAppIimjobs ? {xxs: 700, md: 400} : 700,
                '@media (min-width:320px) and (max-width:480px)': {
                  textAlign: 'center',
                },
              }}
            >
              {heading}
            </Typography>
          </Box>

          <Grid
            container={isAboveMobile ? false : true}
            sx={{
              float: {md: 'left'},
              width: {xxs: '100%', md: '40%'},
              margin: `0 auto ${theme.spacing(5)}`,
              justifyContent: 'center',
            }}
          >
            <Image
              src={appImage || ''}
              alt={downloadText || ''}
              width={isAppIimjobs ? (isAboveMobile ? 290 : 240) : 380}
              height={isAppIimjobs ? (isAboveMobile ? 554 : 440) : 500}
              style={{
                maxWidth: isAppIimjobs
                  ? isBelowActualMobile
                    ? theme.spacing(26.25)
                    : theme.spacing(36.25)
                  : 'auto',
                display: 'block',
                float: 'right',
                objectFit: 'contain',
              }}
              placeholder="blur"
              blurDataURL={appImage}
            />
          </Grid>
          <Box
            sx={{
              float: {md: 'right'},
              width: {xxs: '100%', md: '55%'},
              margin: 0,
              padding: 0,
              textAlign: 'left',
            }}
          >
            {downloadAppContent.map((content, index) => (
              <Box
                key={`homepage-downloadapp-${index}`}
                margin={
                  isAboveMobile
                    ? `${theme.spacing(3.125)} ${theme.spacing(
                        1.25
                      )} ${theme.spacing(3.125)} ${theme.spacing(0)}`
                    : `${theme.spacing(1.875)} ${theme.spacing(
                        0.625
                      )} ${theme.spacing(1.875)} ${theme.spacing(0)}`
                }
              >
                <Typography
                  component="p"
                  sx={{
                    background: dotSvg,
                    paddingLeft: theme.spacing(2.5),
                    fontSize: isAboveMobile
                      ? theme.typography.pxToRem(15)
                      : theme.typography.pxToRem(18),
                    fontWeight: 700,
                  }}
                >
                  {content.heading}
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontSize: isAboveMobile
                      ? theme.typography.pxToRem(15)
                      : theme.typography.pxToRem(18),
                    fontWeight: 400,
                    marginLeft: theme.spacing(2.5),
                  }}
                >
                  {content.text}
                </Typography>
              </Box>
            ))}

            <Typography
              component="p"
              sx={{
                fontStyle: !isAppIimjobs ? 'Nunito Sans' : '',
                fontWeight: !isAppIimjobs ? 300 : 'normal',
                opacity: !isAppIimjobs ? 0.7 : 1,
                fontSize: isAppIimjobs
                  ? isAboveMobile
                    ? theme.typography.pxToRem(15)
                    : theme.typography.pxToRem(18)
                  : '20px',
                margin: isAboveMobile
                  ? `${theme.spacing(1.875)} ${theme.spacing(
                      isAppIimjobs ? 0.25 : 0
                    )}`
                  : `${theme.spacing(6.875)} ${theme.spacing(
                      0.625
                    )} ${theme.spacing(2.5)} ${theme.spacing(0.625)}`,
              }}
            >
              {downloadText}
            </Typography>
            <Box
              textAlign={isAboveMobile ? 'left' : 'center'}
              justifyContent={{xxs: 'center', sm: 'left'}}
              display={'flex'}
              gap={'10px'}
              flexWrap={'wrap'}
            >
              {isAppIimjobs ? (
                downloadAppButton.map((btn, index) => (
                  <Button
                    key={`download-home-app-${index}`}
                    variant="outlined"
                    href={btn['link']}
                    target="_blank"
                    onClick={e => {
                      e.preventDefault();
                      const url = btn['link'];
                      if (url) {
                        openUrl(url, '_blank');
                      }
                      deferTask(() => {
                        handleTracking(btn['eventTag'], {
                          event_category: 'engagement',
                          label: btn['title'],
                          value: `${index}`,
                        });
                      });
                    }}
                    sx={{
                      color: 'text.primary',
                      borderColor: 'secondary.main',
                      borderRadius: 8,
                      fontWeight: isAboveMobile ? 'bold' : 400,
                      fontSize: isAboveMobile
                        ? theme.typography.pxToRem(20)
                        : theme.typography.pxToRem(18),
                      // marginRight: isAboveMobile
                      //   ? theme.spacing(1.25)
                      //   : theme.spacing(1.875),
                      padding: isAboveMobile
                        ? `${theme.spacing(0.625)} ${theme.spacing(3.75)}`
                        : `${theme.spacing(0.25)} ${theme.spacing(1.5)}`,
                      ':hover': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                      },
                      // marginTop: isBelowActualMobile ? '10px' : 0,
                    }}
                    startIcon={
                      btn['title'] === 'Android' ? (
                        <AiFillAndroid />
                      ) : (
                        <AiFillApple />
                      )
                    }
                  >
                    {' '}
                    {btn['title']}
                  </Button>
                ))
              ) : (
                <Box>
                  <Typography
                    fontSize={'15px'}
                    fontStyle={'Nunito Sans'}
                    sx={{
                      opacity: 0.7,
                    }}
                  >
                    Scan To Download
                  </Typography>
                  <Box marginLeft={'-10px'}>
                    <Image
                      src={downloadAppQr || ''}
                      alt={downloadText || ''}
                      width={200}
                      height={200}
                    />
                  </Box>
                  <Typography
                    marginBottom={'10px'}
                    fontSize={'15px'}
                    fontStyle={'Nunito Sans'}
                    sx={{
                      opacity: 0.7,
                    }}
                  >
                    Download app from
                  </Typography>
                  <Box
                    display={'flex'}
                    justifyContent={'flex-start'}
                    alignItems={'center'}
                    gap={0.5}
                    mt={1.25}
                  >
                    <ButtonFun
                      handleClick={() => {
                        handleTracking('jsClickDownloadAppLinkage', {
                          event_category: 'MobileAppPromotion',
                          Origin: router.pathname,
                          UserId: sessionStorage.getItem('userId') || 'NA',
                          Device: sessionStorage.getItem('device') || 'desktop',
                          Device_Type:
                            sessionStorage.getItem('deviceType') || 'web',
                          Status:
                            sessionStorage.getItem('status') || 'LoggedOut',
                        });
                        openUrl(
                          getOldLink(
                            'https://apps.apple.com/in/app/hirist-tech-it-job-search-app/id952697960'
                          ),
                          '_blank'
                        );
                      }}
                      logoSrc={AppleLogoWhite}
                      logoAlt="apple"
                      first="Download on"
                      second="App Store"
                      imgWidth={25}
                      forPlayStore={false}
                      imgHeight={25}
                    />
                    <ButtonFun
                      handleClick={() => {
                        handleTracking('jsClickDownloadAppLinkage', {
                          event_category: 'MobileAppPromotion',
                          Origin: router.pathname,
                          UserId: sessionStorage.getItem('userId') || 'NA',
                          Device: sessionStorage.getItem('device') || 'desktop',
                          Device_Type:
                            sessionStorage.getItem('deviceType') || 'web',
                          Status:
                            sessionStorage.getItem('status') || 'LoggedOut',
                        });
                        openUrl(
                          getOldLink(
                            'https://play.google.com/store/apps/details?id=com.hirist.jobseeker&hl=en_IN'
                          ),
                          '_blank'
                        );
                      }}
                      logoSrc={PlayStore}
                      logoAlt="playStore"
                      first="Get it on"
                      second="Google Play"
                      forPlayStore={true}
                      imgWidth={25}
                      imgHeight={25}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      {!isAppIimjobs && (
        <Box
          sx={{
            marginBottom: '-64px',
          }}
        >
          <HomePageFooter isTransparent={true} />
        </Box>
      )}

      {!isAppIimjobs && (
        <HiristAuthentication
          variant={VersionVariants.VERSION_TWO}
          onSuccess={onLoginSuccess}
          openDialog={enableLoginPopup}
          enableSignIn={true}
          setOpenDialog={() => {
            dispatch({
              type: 'AppContext/set_login_popup',
              payload: false,
            });
          }}
        />
      )}
    </Section>
  );
};
export default DownloadApp;
